import { motion } from "framer-motion";
import { Building2, Hospital, HeartHandshake, Shield, Users, Globe } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const TrustedBy = () => {
  const { t } = useTranslation();
  
  const partners = [{
    name: "European Health Alliance",
    icon: Globe,
    category: "International"
  }, {
    name: "National Health Services",
    icon: Hospital,
    category: "Healthcare"
  }, {
    name: "Medical Research Institute",
    icon: HeartHandshake,
    category: "Research"
  }, {
    name: "Digital Health Consortium",
    icon: Shield,
    category: "Technology"
  }, {
    name: "Patient Care Foundation",
    icon: Users,
    category: "Patient Care"
  }, {
    name: "Healthcare Innovation Hub",
    icon: Building2,
    category: "Innovation"
  }];
  return <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-white via-neutral-50/50 to-secondary/5" />
      
      
      <div className="container-padding relative z-10">
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true
      }}>
          
          <motion.h2 initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} viewport={{
          once: true
        }} className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent py-[10px] px-4 break-words hyphens-auto leading-tight" style={{ wordBreak: 'break-word' }}>{t.trustedBy.title}</motion.h2>
          
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} viewport={{
          once: true
        }} className="text-lg mt-4 max-w-4xl mx-auto text-[#0e2954] py-[2px]">{t.trustedBy.subtitle}</motion.p>
          
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.7
        }} viewport={{
          once: true
        }} className="mt-8 max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-8 bg-gradient-to-br from-white via-neutral-50/80 to-primary/5 p-8 lg:p-12 rounded-3xl border border-primary/10 shadow-xl backdrop-blur-sm relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/10 via-primary/5 to-accent-teal/10 opacity-70 animate-pulse"></div>
              <div className="absolute inset-0 bg-gradient-to-tl from-accent-teal/8 via-transparent to-secondary/8 opacity-50"></div>
              <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-gradient-to-r from-primary/15 to-secondary/15 rounded-full blur-3xl animate-pulse opacity-40"></div>
              <div className="absolute bottom-1/3 right-1/4 w-24 h-24 bg-gradient-to-l from-accent-teal/20 to-primary/10 rounded-full blur-2xl animate-pulse opacity-30" style={{
              animationDelay: '1s'
            }}></div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-tr from-accent-teal/10 to-primary/10 rounded-full blur-2xl"></div>
              <div className="flex-1 relative z-10">
                <img src="/lovable-uploads/vida24LOGO_4.png" alt="Vida24 Logo" className="h-24 mb-6 mx-auto" />
                <p className="leading-relaxed text-xl text-foreground/90 font-medium mb-6">
                  {t.trustedBy.description}
                </p>
                <motion.a href="https://vida24.eu/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-primary to-secondary text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300 inline-block" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }}>
                  {t.trustedBy.learnMore}
                </motion.a>
              </div>
              <div className="flex-shrink-0 relative z-10">
                <img src="/lovable-uploads/66f7b12f-9c3f-451d-8916-47c1764be385.png" alt="Vida24 digital health platform interface showing login screen and mobile app" className="w-full max-w-md h-auto rounded-lg hover:scale-105 transition-transform duration-300" />
              </div>
            </div>
          </motion.div>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <motion.div className="text-center mb-12" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }}>
            <h3 className="text-2xl md:text-3xl font-bold text-primary mb-4 py-[10px]">
              {t.trustedBy.faqTitle}
            </h3>
            <p className="max-w-2xl mx-auto text-[#0e2954] py-[2px]">
              {t.trustedBy.faqSubtitle}
            </p>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4">
            {[{
            question: t.trustedBy.faqs.question1,
            answer: t.trustedBy.faqs.answer1
          }, {
            question: t.trustedBy.faqs.question2,
            answer: t.trustedBy.faqs.answer2
          }, {
            question: t.trustedBy.faqs.question3,
            answer: t.trustedBy.faqs.answer3
          }, {
            question: t.trustedBy.faqs.question4,
            answer: t.trustedBy.faqs.answer4
          }, {
            question: t.trustedBy.faqs.question5,
            answer: t.trustedBy.faqs.answer5
          }, {
            question: t.trustedBy.faqs.question6,
            answer: t.trustedBy.faqs.answer6
          }].map((faq, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} viewport={{
            once: true
          }}>
                <AccordionItem value={`item-${index}`} className="bg-white border border-primary/10 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden mb-4">
                  <AccordionTrigger className="px-6 py-6 hover:no-underline bg-gradient-to-r from-primary/5 to-secondary/5 hover:from-primary/10 hover:to-secondary/10 data-[state=open]:from-primary/10 data-[state=open]:to-secondary/10 transition-all duration-300">
                    <h4 className="text-lg font-semibold text-primary pr-4 py-[2px] text-left">
                      {faq.question}
                    </h4>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 text-neutral-700 leading-relaxed pt-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>)}
          </Accordion>
        </div>

        {/* Trust indicators */}
        
      </div>
    </section>;
};
export default TrustedBy;